import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import DataTable from "@/components/Datatable/Datatable";
import { Column } from "@/components/Datatable/types";
import { Badge } from "@/components/Badge/Badge";
import { Button } from "@/components/ui/button";
import { useImageRemediation } from "../../hooks/useImageRemediation";
import { ThumbnailOffender } from "../../api/getThumbnailOffenders";

const formatSize = (width: number | null, height: number | null): string => {
  if (width == null || height == null) {
    return "Unknown";
  }
  return `${width}×${height}`;
};

const getEditPath = (offender: ThumbnailOffender): string =>
  offender.entityType === "resource"
    ? `/admin/resources/${offender.uuid}/edit`
    : `/admin/events/${offender.uuid}/edit`;

const ImageRemediationPage: React.FC = () => {
  const { offenders, loading } = useImageRemediation();

  const columns = useMemo<Column<ThumbnailOffender>[]>(
    () => [
      {
        id: "thumbnail",
        header: "Preview",
        cell: ({ row }) =>
          row.original.thumbnailUrl ? (
            <img
              alt={row.original.title}
              className="h-12 w-20 rounded border border-gray-200 dark:border-slate-700 object-cover"
              src={row.original.thumbnailUrl}
            />
          ) : (
            <div className="flex h-12 w-20 items-center justify-center rounded border border-dashed border-gray-200 dark:border-slate-700 text-xs text-gray-400 dark:text-gray-500">
              None
            </div>
          ),
      },
      {
        id: "title",
        header: "Title",
        cell: ({ row }) => (
          <span className="text-gray-900 dark:text-slate-100">
            {row.original.title}
          </span>
        ),
      },
      {
        id: "type",
        header: "Type",
        cell: ({ row }) => (
          <Badge className="capitalize" variant="grey">
            {row.original.entityType}
          </Badge>
        ),
      },
      {
        id: "size",
        header: "Current size",
        cell: ({ row }) => (
          <span className="text-gray-500 dark:text-gray-400">
            {formatSize(row.original.width, row.original.height)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <Button asChild size="sm" variant="outline">
            <Link to={getEditPath(row.original)}>Fix</Link>
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <MainPageWrapper
      loading={loading}
      subtitle="Thumbnails must be 16:9 and at least 1280×720"
      title="Image Remediation"
    >
      {!loading && offenders.length === 0 ? (
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-10 text-center text-sm text-gray-500 dark:text-gray-400">
          All thumbnails meet the 16:9 requirement.
        </div>
      ) : (
        <DataTable<ThumbnailOffender>
          columns={columns}
          data={offenders}
          loading={loading}
          noDataMessage="All thumbnails meet the 16:9 requirement."
          onPageChange={() => {}}
          pageIndex={0}
          pageSize={offenders.length || 1}
          rowKeyExtractor={(item) => `${item.entityType}-${item.uuid}`}
          totalCount={offenders.length}
        />
      )}
    </MainPageWrapper>
  );
};

export default ImageRemediationPage;
